/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import type { FC } from 'react';
import React, { useContext, useCallback, useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceAbort,
	ExperienceFailure,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError.ts';
import { fg } from '@confluence/feature-gating';
import { TRIAL_COMPONENTS_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { getLicenseConsumingUserCountFromQuery } from '../graphql/getLicenseConsumingUserCountFromQuery.ts';
import { useHasBillingInformation } from '../useHasBillingInformation.ts';
import { useLocalTrialEndDatesFromQuery } from '../graphql/useLocalTrialEndDatesFromQuery.ts';
import { useBillingUrls } from '../useBillingUrls.ts';
import { usePlanSelectionPageInfo } from '../PremiumReverseTrialsExperiment/usePlanSelectionPageInfo.ts';
import { useIsEligibleForPremiumReverseTrialsExperiment } from '../PremiumReverseTrialsExperiment/useIsEligibleForPremiumReverseTrialsExperiment.ts';
import { useReverseTrialCohort } from '../PremiumReverseTrialsExperiment/useReverseTrialEligible.tsx';
import { BillingSourceSystem } from '../graphql/__types__/TrialDetailsQuery.ts';
import { useAutoUpgradeNotATrialExperiment } from '../AutoUpgradeNotATrialExperiment/useAutoUpgradeNotATrialExperiment.ts';
import { useIsPreDunningPeriod } from '../useIsPreDunningPeriod.ts';

import { TrialFlagsComponent } from './TrialFlagsComponent.tsx';
import type { TrialFlagsTrialDetailsQuery as TrialFlagsTrialDetailsQuery$data } from './__types__/TrialFlagsTrialDetailsQuery.ts';

const DAYS_LEFT_IN_TRIAL_TO_SHOW_FLAGS = [1, 3];
const DAYS_LEFT_IN_PRE_DUNNING_TO_SHOW_FLAGS_AUTO_UPGRADE_NOT_A_TRIAL_EXPERIMENT = [3, 2, 1, -0];
const mapDismissStateKeyInTrial = (daysLeft: number | null) =>
	daysLeft ? `dismissedTrial${daysLeft}DayFlag` : '';
const mapDismissStateKeyInPredunning = (daysLeft?: number) =>
	daysLeft ? `dismissedPredunning${daysLeft}DayFlag` : '';

export const TrialFlags: FC = () => {
	const { isLicensed, isLoggedIn, userId, edition, cloudId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const experienceTracker = useContext(ExperienceTrackerContext);
	const hasExperienceTrackerStartedRef = useRef<Boolean>(false);

	const { setOnboardingState } = useSetOnboardingState();

	const isTrialEdition =
		edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM;

	const skipTrialDetailsQuery =
		!isLicensed || !isLoggedIn || !isSiteAdmin || !userId || !isTrialEdition;

	const {
		data: trialDetailsData,
		loading: trialLoading,
		error: trialError,
	} = useQuery<TrialFlagsTrialDetailsQuery$data>(
		graphql`
			query TrialFlagsTrialDetailsQuery {
				license {
					billingSourceSystem
					licenseConsumingUserCount
					trialEndDate
				}
			}
		`,
		{
			skip: skipTrialDetailsQuery,
		},
	);

	const licenseConsumingUserCount = getLicenseConsumingUserCountFromQuery(trialDetailsData);
	const billingSourceSystem = trialDetailsData?.license?.billingSourceSystem;
	const trialEndDate = trialDetailsData?.license?.trialEndDate;
	const formattedDate = useLocalTrialEndDatesFromQuery(trialDetailsData);
	const { deactivationDate: deactivationDateTrial } = formattedDate || {};
	const trialDaysLeft = getTrialDaysLeftFromTrialEndDate(trialEndDate) ?? null;

	const isInTrial = !!trialEndDate && trialDaysLeft && trialDaysLeft >= 0;

	const skipPredunningQuery =
		skipTrialDetailsQuery || isInTrial || billingSourceSystem !== BillingSourceSystem.CCP;

	const {
		deactivationDate: preDunningDeactivationDateResult,
		isPreDunningPeriod,
		errorSource: predunningErrorSource,
		loading: predunningLoading,
		error: predunningError,
	} = useIsPreDunningPeriod({ skip: skipPredunningQuery });

	const { daysLeftUntilDeactivation, date: deactivationDatePreDunning } =
		preDunningDeactivationDateResult || {};

	const isInTrialOrPredunning = isInTrial || isPreDunningPeriod;

	const isValidTrialDayToShowFlag =
		!!trialDaysLeft && DAYS_LEFT_IN_TRIAL_TO_SHOW_FLAGS.includes(trialDaysLeft);

	const isValidPredunningDayToShowFlag =
		isPreDunningPeriod &&
		daysLeftUntilDeactivation !== undefined &&
		DAYS_LEFT_IN_PRE_DUNNING_TO_SHOW_FLAGS_AUTO_UPGRADE_NOT_A_TRIAL_EXPERIMENT.includes(
			daysLeftUntilDeactivation,
		) &&
		fg('auto_upgrade_not_a_trial_experiment_connie_gate');

	const dismissedFlagKey = isPreDunningPeriod
		? mapDismissStateKeyInPredunning(daysLeftUntilDeactivation)
		: mapDismissStateKeyInTrial(trialDaysLeft);
	let onboardingFlagKey:
		| typeof TRIAL_COMPONENTS_EXPERIENCES.TRIAL_1_DAY_FLAG_DISMISSED
		| typeof TRIAL_COMPONENTS_EXPERIENCES.TRIAL_3_DAY_FLAG_DISMISSED
		| typeof TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_0_DAY_FLAG_DISMISSED
		| typeof TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_1_DAY_FLAG_DISMISSED
		| typeof TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_2_DAY_FLAG_DISMISSED
		| typeof TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_3_DAY_FLAG_DISMISSED
		| typeof TRIAL_COMPONENTS_EXPERIENCES.TRIAL_UNHANDLED_DAY_FLAG_DISMISSED;
	if (trialDaysLeft === 1) {
		onboardingFlagKey = TRIAL_COMPONENTS_EXPERIENCES.TRIAL_1_DAY_FLAG_DISMISSED;
	} else if (trialDaysLeft === 3) {
		onboardingFlagKey = TRIAL_COMPONENTS_EXPERIENCES.TRIAL_3_DAY_FLAG_DISMISSED;
	} else if (daysLeftUntilDeactivation === 3) {
		onboardingFlagKey = TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_3_DAY_FLAG_DISMISSED;
	} else if (daysLeftUntilDeactivation === 2) {
		onboardingFlagKey = TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_2_DAY_FLAG_DISMISSED;
	} else if (daysLeftUntilDeactivation === 1) {
		onboardingFlagKey = TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_1_DAY_FLAG_DISMISSED;
	} else if (daysLeftUntilDeactivation === 0) {
		onboardingFlagKey = TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_0_DAY_FLAG_DISMISSED;
	} else {
		// This shouldn't actually get used since the query will be skipped and the
		// component will return null. We just need to make sure we're passing a valid
		// key to the `useGetOnboardingState` hook.
		onboardingFlagKey = TRIAL_COMPONENTS_EXPERIENCES.TRIAL_UNHANDLED_DAY_FLAG_DISMISSED;
	}

	const skipCommerceQueries = !(isValidTrialDayToShowFlag || isValidPredunningDayToShowFlag);

	const {
		hasBillingInformation,
		loading: billingLoading,
		error: billingErr,
	} = useHasBillingInformation(skipCommerceQueries);

	const skipGetOnboardingStateQuery = skipCommerceQueries || hasBillingInformation;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateErr,
	} = useGetOnboardingState(onboardingFlagKey, skipGetOnboardingStateQuery);

	const data = deserializeState(onboardingStateData);
	const hasDismissedFlag = data[dismissedFlagKey];

	const {
		loading: useAutoUpgradeNotATrialExperimentLoading,
		error: useAutoUpgradeNotATrialExperimentError,
		autoUpgradeNotATrialExperimentCohort,
	} = useAutoUpgradeNotATrialExperiment({
		isSiteAdmin,
		edition,
		hasBillingInformation,
		commerceBackendName: billingSourceSystem,
		isInTrialOrPredunning,
		source: 'confluence_trial_flag',
		analyticAttributes: { trialDaysLeft },
		skip:
			!fg('auto_upgrade_not_a_trial_experiment_connie_gate') ||
			!(isValidPredunningDayToShowFlag || isValidTrialDayToShowFlag),
	});

	const shouldSkipPremiumReverseTrialsCheck =
		skipCommerceQueries ||
		edition !== ConfluenceEdition.PREMIUM ||
		hasBillingInformation ||
		!fg('growth_confluence_buy_reverse_trials_fg');

	const {
		isReverseTrialer,
		loading: isReverseTrialLoading,
		error: isReverseTrialerError,
	} = useReverseTrialCohort({
		skip: shouldSkipPremiumReverseTrialsCheck,
	});

	const {
		isAvailableToUser: isPlanSelectionPageUrlAvailable,
		planSelectionPageUrl,
		error: pspUrlError,
		loading: isPSPUrlLoading,
	} = usePlanSelectionPageInfo({
		skip: shouldSkipPremiumReverseTrialsCheck,
	});

	const isEligibleForPremiumReverseTrialsExperiment =
		useIsEligibleForPremiumReverseTrialsExperiment({
			isReverseTrialer,
			isInTrial: (trialDaysLeft ?? 0) > 0,
			edition,
			hasBillingInformation,
			commerceBackendName: billingSourceSystem as BillingSourceSystem,
			isPlanSelectionPageUrlAvailable,
		});

	const {
		paymentFlowUrl,
		loading: urlLoading,
		error: urlErr,
	} = useBillingUrls(skipCommerceQueries || autoUpgradeNotATrialExperimentCohort === 'treatment');

	const onDismissFlag = useCallback(() => {
		if (!hasDismissedFlag) {
			void setOnboardingState({
				key: onboardingFlagKey,
				value: 'true',
			});
		}
	}, [onboardingFlagKey, hasDismissedFlag, setOnboardingState]);

	const canTrackExperience =
		!skipTrialDetailsQuery && !skipCommerceQueries && !skipGetOnboardingStateQuery;

	if (!hasExperienceTrackerStartedRef.current && canTrackExperience) {
		experienceTracker.start({
			name: TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE,
		});
		hasExperienceTrackerStartedRef.current = true;
	}

	const loading =
		trialLoading ||
		urlLoading ||
		billingLoading ||
		onboardingStateLoading ||
		isReverseTrialLoading ||
		isPSPUrlLoading ||
		useAutoUpgradeNotATrialExperimentLoading ||
		predunningLoading;

	// `TrialDetailsQuery` can throw Permission exception if initiater doesn't have sufficient permission
	// Permission error in this case is expected and FE should handle it gracefully - "markErrorAsHandled" and abort the experience instead of reporting as failures.
	if (trialError && isUnauthorizedError(trialError)) {
		markErrorAsHandled(trialError);
		return (
			<ExperienceAbort
				name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE}
				reason="Aborted due to insufficient permission"
			/>
		);
	}

	// `useHasBillingInformation` can cause a rate limiting error
	if (billingErr && isTooManyRequestsError(billingErr)) {
		markErrorAsHandled(billingErr);
		return (
			<ExperienceAbort
				name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE}
				reason="Aborted due to rate limit exceeded error"
			/>
		);
	}

	const error =
		trialError ||
		urlErr ||
		billingErr ||
		onboardingStateErr ||
		pspUrlError ||
		isReverseTrialerError ||
		useAutoUpgradeNotATrialExperimentError ||
		predunningError;

	const shouldShowFlag =
		autoUpgradeNotATrialExperimentCohort === 'treatment'
			? isValidPredunningDayToShowFlag
			: isValidTrialDayToShowFlag;

	if (error) {
		return (
			<ExperienceFailure
				name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE}
				error={error}
				attributes={{
					trialErr: trialError?.message,
					urlErr: urlErr?.message,
					billingErr: billingErr?.message,
					onboardingStateErr: onboardingStateErr?.message,
					pspUrlError: pspUrlError?.message,
					isReverseTrialerError: isReverseTrialerError?.message,
					useAutoUpgradeNotATrialExperimentError: useAutoUpgradeNotATrialExperimentError?.message,
					predunningError: predunningError?.message,
					predunningErrorSource,
				}}
			/>
		);
	}

	if (loading) return null;

	if (
		skipGetOnboardingStateQuery ||
		!onboardingStateData ||
		!shouldShowFlag ||
		hasDismissedFlag ||
		!isTrialEdition
	) {
		experienceTracker.abort({
			name: TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE,
			reason: 'Aborted because a pre-requisite was not met',
		});
		return null;
	}

	return (
		<RelayEnvironmentProvider>
			<TrialFlagsComponent
				edition={edition}
				daysLeftInPredunning={daysLeftUntilDeactivation}
				deactivationDate={deactivationDateTrial || deactivationDatePreDunning}
				licenseConsumingUserCount={licenseConsumingUserCount}
				linkToAddBilling={paymentFlowUrl}
				trialDaysLeft={trialDaysLeft}
				cloudId={cloudId}
				onDismiss={onDismissFlag}
				isEligibleForPremiumReverseTrialsExperiment={isEligibleForPremiumReverseTrialsExperiment}
				planSelectionPageUrl={planSelectionPageUrl}
				autoUpgradeNotATrialExperimentCohort={autoUpgradeNotATrialExperimentCohort}
			/>
			<ExperienceSuccess
				name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE}
				attributes={{
					autoUpgradeNotATrialExperimentCohort,
					daysLeftUntilDeactivation,
					trialDaysLeft,
				}}
			/>
		</RelayEnvironmentProvider>
	);
};
